import React, { useState, useEffect, forwardRef } from "react";
import styled from "styled-components";
import { useInView } from "react-intersection-observer";
import { useRenderedOnClient } from "../hooks/use-rendered-on-client";

const SIZE_BREAKPOINTS = {
  xl: 1600,
  l: 1201,
  m: 1024,
  s: 768,
  xs: 480,
};

const Img = styled.img`
  max-width: 100%;
  margin: 0 auto;
  display: block;
`;

const ImageLoader = forwardRef((props, ref) => {
  const {
    source,
    viewports,
    viewportsRatio,
    alt,
    imgProps = {},
    defaultImage,
    ratio,
    shouldLoad = true,
  } = props;
  const getViewport = (size) => {
    if (size >= SIZE_BREAKPOINTS.xl) return "xxl";
    else if (size >= SIZE_BREAKPOINTS.l) return "xl";
    else if (size >= SIZE_BREAKPOINTS.m) return "l";
    else if (size >= SIZE_BREAKPOINTS.s) return "m";
    else if (size >= SIZE_BREAKPOINTS.xs) return "s";
    else return "xs";
  };

  const autoFillViewport = (desiredViewport, obj) => {
    if (obj.hasOwnProperty(desiredViewport)) {
      return obj[desiredViewport];
    } else if (desiredViewport == SIZE_BREAKPOINTS.xs) {
      return null;
    }
    const nextKey = Object.keys(
      SIZE_BREAKPOINTS
    ).reduce((acc, item, idx, arr) =>
      desiredViewport == arr[idx - 1] ? item : acc
    );
    return autoFillViewport(nextKey, obj);
  };
  const renderedOnClient = useRenderedOnClient();
  const [sourceLoaded, setSourceLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentViewport, setCurrentViewport] = useState(
    getViewport(renderedOnClient ? window.innerWidth : 1920)
  );

  const currentSource = viewports
    ? viewports.hasOwnProperty(currentViewport)
      ? viewports[currentViewport]
      : autoFillViewport(currentViewport, viewports) || source
    : source;

  const currentRatio = viewportsRatio
    ? viewportsRatio.hasOwnProperty(currentViewport)
      ? viewportsRatio[currentViewport]
      : autoFillViewport(currentViewport, viewportsRatio) || ratio
    : ratio || "1:1";

  useEffect(() => {
    let mounted = true;
    if (shouldLoad) {
      new Promise((resolve, reject) => {
        const img = new Image();
        img.src = currentSource;
        img.onload = resolve;
        img.onerror = reject;
      })
        .then(() => {
          if (mounted) {
            setSourceLoaded(true);
            setHasError(false);
          }
        })
        .catch((e) => {
          if (mounted) {
            console.log(e);
            setSourceLoaded(true);
            setHasError(true);
          }
        });
    }
    return () => (mounted = false);
  }, [currentSource, shouldLoad]);

  useEffect(() => {
    let timeout = null;
    const listener = () => {
      clearTimeout(timeout);
      timeout = setTimeout(
        () => setCurrentViewport(getViewport(window.innerWidth)),
        200
      );
    };
    listener();
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, []);

  if (sourceLoaded) {
    return (
      <Img
        src={hasError ? defaultImage : currentSource}
        alt={alt}
        {...imgProps}
      />
    );
  } else {
    return (
      <div ref={ref} className="image-loading">
        <span
          style={{
            display: "block",
            width: "100%",
            paddingTop:
              "calc(" +
              currentRatio.split(":")[1] +
              " / " +
              currentRatio.split(":")[0] +
              " * 100%)",
          }}
        />
      </div>
    );
  }
});

const ImageLoaderInViewWrapper = ({ shouldLoad = true, ...props }) => {
  const [ref, inView, entry] = useInView({
    treshold: 0.05,
  });

  return (
    <ImageLoader
      {...props}
      ref={ref}
      shouldLoad={inView ? shouldLoad : false}
    />
  );
};

export default ImageLoaderInViewWrapper;
